import { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { X } from 'react-feather';
import { TagDetailsSectionEditButtons } from './TagDetailsSectionEditButtons';

export type TagDetailsCustomSummaryPromptsProps = {
  title?: string;
  prompts: string[];
  /** If provided, an edit button is shown and prompts become editable (uncontrolled mode). */
  onSave?: (prompts: string[]) => void | Promise<void>;
  isSaving?: boolean;
  /** When true, the built-in title/divider header is suppressed. */
  hideHeader?: boolean;
  // ── Controlled mode (used by TagDetailsCustomSummarySection) ──────────────
  /** When provided the component becomes controlled: internal editing state is unused. */
  isEditing?: boolean;
  /** The draft prompts list in controlled mode. */
  local?: string[];
  /** Called when the user changes a prompt in controlled mode. */
  onLocalChange?: (prompts: string[]) => void;
};

export const TagDetailsCustomSummaryPrompts = ({
  title = 'Custom Summary Prompt',
  prompts: initialPrompts,
  onSave,
  isSaving = false,
  hideHeader = false,
  isEditing: isEditingProp,
  local: localProp,
  onLocalChange,
}: TagDetailsCustomSummaryPromptsProps) => {
  const isControlled = isEditingProp !== undefined;
  const empty = [''];
  const normalize = (p: string[]) => (p.length > 0 ? p : empty);

  const [saved, setSaved] = useState(() => normalize(initialPrompts));
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [internalLocal, setInternalLocal] = useState<string[]>(() => normalize(initialPrompts));

  const isEditing = isControlled ? isEditingProp! : internalIsEditing;
  const local = isControlled ? (localProp ?? normalize(initialPrompts)) : internalLocal;

  useEffect(() => {
    if (isControlled) return;
    const next = normalize(initialPrompts);
    setSaved(next);
    if (!internalIsEditing) setInternalLocal(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompts]);

  const handleEdit = useCallback(() => {
    setInternalLocal([...saved]);
    setInternalIsEditing(true);
  }, [saved]);

  const handleCancel = useCallback(() => {
    setInternalLocal([...saved]);
    setInternalIsEditing(false);
  }, [saved]);

  const handleSave = useCallback(async () => {
    const filtered = internalLocal.map((p) => p.trim()).filter((p) => p.length > 0);
    if (!onSave) return;
    await onSave(filtered);
    setSaved(filtered.length > 0 ? filtered : empty);
    setInternalLocal(filtered.length > 0 ? filtered : empty);
    setInternalIsEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalLocal, onSave]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (isControlled) {
        const next = [...local];
        next[index] = value;
        onLocalChange?.(next);
      } else {
        setInternalLocal((prev) => {
          const next = [...prev];
          next[index] = value;
          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isControlled, local, onLocalChange]
  );

  const handleAdd = useCallback(() => {
    if (isControlled) {
      onLocalChange?.([...local, '']);
    } else {
      setInternalLocal((prev) => [...prev, '']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, local, onLocalChange]);

  const handleDelete = useCallback(
    (index: number) => {
      if (isControlled) {
        onLocalChange?.(local.filter((_, i) => i !== index));
      } else {
        setInternalLocal((prev) => prev.filter((_, i) => i !== index));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isControlled, local, onLocalChange]
  );

  const displayPrompts = isEditing ? local : saved;

  return (
    <Root>
      {!hideHeader && !isControlled && (
        <>
          <HeaderRow>
            <Title>{title}</Title>
            {onSave && (
              <Actions>
                <TagDetailsSectionEditButtons
                  isEditing={isEditing}
                  isSaving={isSaving}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              </Actions>
            )}
          </HeaderRow>
          <Divider />
        </>
      )}
      <PromptsContainer>
        {displayPrompts.map((prompt, index) => (
          <PromptRow key={index}>
            <Textarea
              value={prompt}
              onChange={(e) => handleChange(index, e.target.value)}
              placeholder="Define a custom prompt for AI summary generation..."
              rows={2}
              readOnly={!isEditing}
            />
            {isEditing && index > 0 && (
              <DeleteButton onClick={() => handleDelete(index)} type="button" title="Delete this prompt">
                <X size={14} />
              </DeleteButton>
            )}
          </PromptRow>
        ))}
        {isEditing && (
          <AddButton onClick={handleAdd} type="button">
            + Add Prompt
          </AddButton>
        )}
      </PromptsContainer>
    </Root>
  );
};

TagDetailsCustomSummaryPrompts.displayName = 'TagDetails.CustomSummaryPrompts';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const Title = styled.div`
  font-family: var(--font-semibold);
  font-size: 16px;
  color: var(--black);
`;

const Divider = styled.div`
  height: 1px;
  background: var(--gray-l);
  margin-bottom: 12px;
`;

const PromptsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PromptRow = styled.div`
  position: relative;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 10px;
`;

const Textarea = styled.textarea`
  flex: 1;
  font-size: 14px;
  padding: 8px;
  border: 1px solid var(--gray-l);
  border-radius: 2px;
  resize: vertical;
  font-family: inherit;
  background: var(--white);
  color: var(--black);

  ::placeholder {
    color: var(--gray);
  }

  &:focus {
    outline: none;
    border-color: var(--pri-02);
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }

  &[readonly] {
    background: var(--gray-xl);
    cursor: default;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--gray-l);
  background: var(--white);
  cursor: pointer;
  color: var(--gray-d);
  flex-shrink: 0;
  margin-top: 8px;

  &:hover {
    border-color: var(--error);
    color: var(--error);
  }
`;

const AddButton = styled.button`
  align-self: flex-start;
  background: var(--white);
  color: var(--pri-02);
  border: 1px solid var(--pri-02);
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: var(--pri-02);
    color: white;
  }
`;
